import type { BeforeRequestHook } from 'ky';
import { extractInterfaceFromRoute } from '../../../extractInterfaceFromRoute';
import { HTTP_HEADERS } from '../../../../auth/const';
import { isServer } from '../../utils';
import type { HookGetterArgs } from '../types';
import { requestWithNewUrl } from '../../../request';
import isNative from '../../../native/isNative';
import { getInternalSvcRequest } from './internalSvc';

/**
 * Converts the given URL to a public API URL
 * @param url - The URL to convert in string
 */
export function publicApiURL(url: string): string {
  const urlObj = new URL(url);
  if (urlObj.pathname.startsWith('/api/')) {
    urlObj.pathname = urlObj.pathname.replace('/api/', '/api-endpoint/external/');
  }
  return urlObj.toString();
}

const makePublicRequest = async (request: Request): Promise<Request> => {
  const newUrl = publicApiURL(request.url);

  const newRequest = await requestWithNewUrl(request, newUrl);
  // remove after ky issue is fixed https://github.com/sindresorhus/ky/issues/387
  // returning from a hook will block the next hooks. so we need to call the function and return the value
  return (await getInternalSvcRequest(newRequest)) || newRequest;
};

export const getInterfaceHeaders = ({
  fetchArgs: { url },
  options,
}: HookGetterArgs): BeforeRequestHook => {
  const interfaceHeadersHook: BeforeRequestHook = async (request) => {
    if (isServer() && url.startsWith('/')) {
      const { headers: nextHeaders } = await import('next/headers');
      const header = await nextHeaders();

      const interfaceId = extractInterfaceFromRoute(header.get('pathname') ?? '');
      if (interfaceId) {
        request.headers.set(HTTP_HEADERS.AppId, interfaceId);
      }

      if (header.get(HTTP_HEADERS.PUBLIC)) {
        return makePublicRequest(request);
      }
    }

    if (!isServer() && !isNative()) {
      const interfaceId = extractInterfaceFromRoute(window.location.pathname);
      if (interfaceId) {
        request.headers.set(HTTP_HEADERS.AppId, interfaceId);
      }

      if (options.meta?.isPublicInterface) {
        return makePublicRequest(request);
      }
    }
  };
  return interfaceHeadersHook;
};
